import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, CreditCard, FileText, AlertCircle, ArrowLeft, Lock, ExternalLink, Phone, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRACTICE_NAME = "Protea Health Ltd";
const PRACTICE_WEBSITE = "https://adhdclinicjersey.proteahealth.co.uk";
const PRACTICE_PHONE = "01534 484191";

// Direct SumUp payment link
const SUMUP_PAYMENT_LINK = "https://pay.sumup.com/b2c/X1N1O42M34?checkout_id=acbcc646-5fd2-4d85-801a-8c1346400ac5";

export default function PaymentPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [paymentLinkClicked, setPaymentLinkClicked] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("patientData");
    if (!stored) {
      toast.error("Please verify your identity first");
      navigate("/start");
      return;
    }
    setPatientData(JSON.parse(stored));

    // Restore gate state: if user already clicked the SumUp link, show step 2
    if (sessionStorage.getItem("paymentLinkClicked") === "true") {
      setPaymentLinkClicked(true);
    }
  }, [navigate]);

  // Create checkout record in our system (called on page load, silently)
  useEffect(() => {
    const createCheckoutIfNeeded = async () => {
      if (sessionStorage.getItem("checkoutId")) return;
      const stored = sessionStorage.getItem("patientData");
      if (!stored) return;
      const pd = JSON.parse(stored);
      try {
        const response = await axios.post(`${API}/payments/checkout`, {
          patient_id: pd.patient_id,
          first_name: pd.first_name,
          last_name: pd.last_name,
          email: pd.email
        });
        sessionStorage.setItem("checkoutId", response.data.checkout_id);
        sessionStorage.setItem("checkoutRef", response.data.checkout_reference);
      } catch (err) {
        console.error("Failed to pre-create checkout:", err);
      }
    };
    createCheckoutIfNeeded();
  }, []);

  // Called when user clicks the Pay button
  const handlePayLinkClick = (e) => {
    e.preventDefault();
    // Save state BEFORE opening the new window
    sessionStorage.setItem("paymentLinkClicked", "true");
    setPaymentLinkClicked(true);
    // Force open in new tab/window — user stays on this page
    const newWindow = window.open(SUMUP_PAYMENT_LINK, "_blank");
    if (!newWindow || newWindow.closed) {
      // Popup was blocked — show toast with instructions
      toast.info("Please allow popups, or use the link below to open the payment page in a new tab.");
    }
  };

  const handleConfirmAndContinue = async () => {
    if (!confirmationChecked) {
      toast.error("Please tick the confirmation box first");
      return;
    }

    const checkoutId = sessionStorage.getItem("checkoutId");
    if (!checkoutId) {
      toast.error("Payment session error. Please refresh and try again.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/payments/mark-pending-verification/${checkoutId}`);
      const fingerInkUrl = response.data.finger_ink_url;

      if (fingerInkUrl) {
        setRedirecting(true);
        toast.success("Payment confirmed! Opening your form now...");
        // Clean up session
        sessionStorage.removeItem("paymentLinkClicked");
        sessionStorage.removeItem("checkoutId");
        sessionStorage.removeItem("checkoutRef");
        // Redirect to Finger-Ink form
        window.location.href = fingerInkUrl;
      } else {
        navigate(`/payment-success?checkout_ref=${checkoutId}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please contact support.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!patientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Confirmed</h2>
          <p className="text-slate-600">Redirecting you to your prescription request form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/start")}
            className="flex items-center text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="btn-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>
          <span className="text-sm font-medium text-slate-700">{PRACTICE_NAME}</span>
        </div>

        <div className="text-center mb-8 animate-fade-in">
          <h1 className="heading-primary text-3xl sm:text-4xl mb-2">Payment Required</h1>
          <p className="text-body">Complete payment to access your prescription request form</p>
        </div>

        <Card className="card-elevated animate-fade-in" style={{ animationDelay: "0.1s" }} data-testid="payment-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-sky-500" />
              Order Summary
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Service details */}
            <div className="bg-slate-50 rounded-xl p-5">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="font-medium text-slate-900">Private Medication Review</p>
                  <p className="text-sm text-slate-500 mt-1">Prescribing & Monitoring Fee</p>
                </div>
                <p className="text-2xl font-bold text-slate-900">£5.00</p>
              </div>
              <Separator className="my-4" />
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 text-slate-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                  Clinical review by qualified prescriber
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                  Medication assessment and monitoring
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                  Prescription if clinically appropriate
                </p>
              </div>
            </div>

            {/* Important notice */}
            <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Important</p>
                <p className="text-amber-700 mt-1">
                  Payment does not guarantee a prescription. Clinical decisions are made by qualified healthcare professionals.
                </p>
              </div>
            </div>

            {/* Patient info */}
            <div className="text-sm text-slate-600">
              <p>Paying as: <span className="font-medium text-slate-900">{patientData.first_name} {patientData.last_name}</span></p>
              <p>Email: <span className="font-medium text-slate-900">{patientData.email}</span></p>
            </div>

            {/* ===== GATED FLOW ===== */}
            {!paymentLinkClicked ? (
              /* STEP 1: Pay button — must click this first */
              <div className="space-y-4">
                <button
                  onClick={handlePayLinkClick}
                  className="flex items-center justify-center gap-2 w-full py-4 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl transition-colors text-lg cursor-pointer"
                  data-testid="btn-pay-sumup"
                >
                  <CreditCard className="h-5 w-5" />
                  Pay £5.00 with SumUp
                </button>
                <p className="text-center text-sm text-slate-500">
                  SumUp will open in a new window.
                  <br />After paying, return to this page to access your form.
                </p>
              </div>
            ) : (
              /* STEP 2: Confirm payment & continue to form */
              <div className="space-y-4">
                {/* Status banner */}
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-center">
                  <p className="font-medium text-sky-800">
                    Payment page opened. After paying, confirm below.
                  </p>
                  <a
                    href={SUMUP_PAYMENT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 underline mt-2"
                    data-testid="btn-reopen-payment"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Re-open SumUp payment page
                  </a>
                </div>

                {/* Confirmation checkbox */}
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-slate-200">
                  <Checkbox
                    id="confirm-payment"
                    checked={confirmationChecked}
                    onCheckedChange={setConfirmationChecked}
                    className="mt-0.5"
                    data-testid="checkbox-confirm-payment"
                  />
                  <label htmlFor="confirm-payment" className="text-sm text-slate-700 cursor-pointer">
                    I confirm that I have completed the £5.00 payment via SumUp. I understand that my payment will be verified and false claims may result in service denial.
                  </label>
                </div>

                {/* Continue button */}
                <Button
                  onClick={handleConfirmAndContinue}
                  className={`w-full py-4 text-lg font-semibold rounded-xl ${confirmationChecked ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-300 cursor-not-allowed text-slate-500'}`}
                  disabled={isLoading || !confirmationChecked}
                  data-testid="btn-continue-to-form"
                >
                  {isLoading ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                  )}
                  Continue to Prescription Request Form
                </Button>

                {/* Warning */}
                <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    <strong>Note:</strong> All payments are verified against our records. Proceeding without payment will be flagged and may result in service denial.
                  </p>
                </div>
              </div>
            )}

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-3 pt-4">
              <div className="trust-badge">
                <Lock className="h-3.5 w-3.5 text-sky-500" />
                <span>256-bit SSL</span>
              </div>
              <div className="trust-badge">
                <ShieldCheck className="h-3.5 w-3.5 text-sky-500" />
                <span>3D Secure</span>
              </div>
              <div className="trust-badge">
                <CreditCard className="h-3.5 w-3.5 text-sky-500" />
                <span>SumUp</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          Your payment is processed securely by SumUp. We never store your card details.
        </p>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-slate-200">
          <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            <span>{PRACTICE_NAME}</span>
            <a href={`tel:${PRACTICE_PHONE}`} className="hover:text-sky-600 flex items-center gap-1">
              <Phone className="h-3 w-3" />{PRACTICE_PHONE}
            </a>
            <a href={PRACTICE_WEBSITE} target="_blank" rel="noopener noreferrer" className="hover:text-sky-600 flex items-center gap-1">
              <Globe className="h-3 w-3" />Website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
